import type { ReactNode } from 'react'
import { Logo } from '../Logo/Logo'
import './FooterBand.css'

export interface FooterBandProps {
  contact: ReactNode
  social: ReactNode
  hours: ReactNode
  partners: ReactNode
  legal: ReactNode
  copyright?: ReactNode
  logoSize?: number
}

/**
 * Leaf-only brand mark (no wordmark), extracted from the same source group
 * as `Logo`. Used here as a decorative watermark — `fill="currentColor"`
 * so the caller's CSS `color` controls it, not a fixed brand color.
 */
function LeafGlyph({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="320.88 -25.91 1374.16 1374.16"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M1373.8,576.28c-31.72,66.07-69.07,215.08-162.15,235.34-4.89.98-9.69,2.39-14.18,4.64-13.82,6.63-28.35,14.53-41.65,23.13-12.03,7.69-23.97,16.55-36.34,24.42-7.83,4.84-15.41,9.78-24.23,12.41-35.32,10.16-10.94-49.31-3.61-67.87,20.57-50.72,51.21-97.41,71.67-147.82,36.11-125.08,36.12-179.1,181-207.75,45.89-12.02,100.81-9.57,146.91-7.62,2.38.07,5.09.58,6.59,2.34,1.68,1.93,1.73,5.41,1.52,8.12-5.54,84.53-21.38,174.5-72.57,244.55-36.35,48.9-91.31,82.22-149.99,97.6-1.83.41-3.32.53-4.04.1-1.65-.75,54.2-63.95,55.2-65.47,41.96-65.25,29.67-79.45,45.88-156.08v-.03Z" />
      <path d="M879.49,1031.06c16.29-23.26,31.84-47.03,49.83-69.09,1.68-2.45,8.85-9.54,3.48-10.26-45.73-4.91-241.45-8.91-281.52-12.23-.84-.14.11-.32,2.71-.53,1.13-.09,2.54-.19,4.06-.29,70.8-4.74,184.25-9.17,256.3-13.39,10.47-1.49.3-9.06-3.99-12.16-117.84-89.61-253.34-136.04-378.26-31.8-10.44,8.83-43.57,37.66-44.57,49.37-.46,3.83,1.01,7.73,3.06,11.34,79.37,127.51,285.57,233.16,388.76,89.23l.14-.19Z" />
      <path d="M1528.38,930.39c-.38-2.61-2.08-5.37-3.89-7.43-11.65-13.14-24.24-25.77-37.59-37.55-36.12-32.13-80.61-56.57-129.19-61.92-95.59-13.08-190.16,40.67-261.59,99.84-.14.79,1.19,1.37,2.41,1.56,71.09,4.01,193.92,9.8,264.17,13.8,2.4.2,6.34.21,8.08.55-.06.03-.33.05-.66.07-18.37.76-49.58,2.03-84.89,3.48-84.91,3.96-192.23,6.91-203.95,8.87.15,1.47,1.77,3.13,2.72,4.39,12.12,15.17,23.72,30.95,34.94,46.98,9.1,12.71,17.49,25.73,26.82,38.16,111.03,132.75,304.98,19.87,380.56-102.99,1.42-2.45,2.42-5.31,2.09-7.6l-.03-.2Z" />
      <path d="M869.79,1124.99c58.07-28.95,109.16-96.25,107.98-162.11-64.18,19.19-102.51,98.94-108.06,162.08l.09.03Z" />
      <path d="M1145.73,1124.16c1.59-1.29,1.09-5.69.75-7.76-8.82-60.27-43.79-130.56-101.68-153.13-1.96-.55-3.68-.6-4.8.19-2.05,1.35-1.97,5.55-1.75,8.01,5.3,61.38,47.73,121.69,101.1,151.39,1.51.93,4.93,2.39,6.27,1.37l.11-.09Z" />
      <path d="M1050.08,865.58c-4.02,8.66-11.06,22.3-18.34,25.26-10,3.2-12.13-15.21-13.12-23.15-6.5-116.1,7.49-143.49,3.03-259.29-.13-3.91-14.13-129.68-15.78-113.91-1.88,29.68-15.81,87.25-16.79,116.98-2.59,95,11.42,170.9,7.18,262.09-.93,8.79-2.42,23.63-11.14,23.34-5.47-.99-9.52-6.31-14.53-14.14-5.41-9-10.08-19.31-14.75-28.9-85.16-172.56-165.1-368.27-50.1-534.82,10.79-17.37,21.48-33.87,32.8-50.33,11.19-16.2,22.68-32.19,34.75-47.59,8.53-10.55,19.03-23.53,33.56-23.77h.16c16.55.85,26.74,15.92,37.25,28.57,6.75,8.63,13.39,17.34,19.92,26.13,47.25,63.53,89.28,134.44,100.26,214.03,19.46,140.82-55.44,272.09-114.36,399.5" />
      <path d="M640.84,576.45c31.75,66.05,69.17,215.05,162.26,235.27,4.89.98,9.69,2.39,14.18,4.63,13.83,6.62,28.36,14.52,41.66,23.12,12.04,7.69,23.98,16.54,36.35,24.4,7.83,4.84,15.42,9.77,24.23,12.4,35.32,10.14,10.92-49.32,3.58-67.88-20.6-50.71-51.26-97.38-71.73-147.79-36.17-125.07-36.2-179.08-181.09-207.67-45.89-12-100.82-9.52-146.91-7.56-2.38.08-5.09.58-6.58,2.34-1.68,1.93-1.73,5.41-1.52,8.12,5.57,84.52,21.46,174.49,72.68,244.51,36.37,48.88,91.35,82.18,150.04,97.53,1.83.41,3.32.53,4.04.1,1.65-.75-.33-4.66-1.7-5.87-86.97-76.86-83.24-139.02-99.49-215.63v-.03Z" />
    </svg>
  )
}

interface Petal {
  key: string
  content: ReactNode
  className?: string
}

/**
 * Full-width dark footer band, structured as 5 zones around the centered
 * logo — echoes the brand mark's 5-petal shape (Cycle 12). A giant, low-opacity
 * leaf watermark sits behind the content as the section's signature element.
 *
 * Any of the four outer zones (contact, social, hours, partners) may be
 * omitted by passing `null`/`undefined`/`false` — e.g. no partners programme
 * yet, or no social links configured, including both zones on the same side
 * at once. An omitted zone's wrapper is not rendered at all, rather than
 * rendered empty: CSS Grid allocates a track's `1fr` share whether or not
 * its cell has content, so a rendered-but-empty petal leaves a permanent
 * blank column, not an adaptive one.
 *
 * The desktop row is exactly three grid tracks — `1fr auto 1fr` — always,
 * regardless of how many zones are populated: the two outer tracks are
 * always equal, so the centered `auto` track (the logo) never drifts, and
 * `repeat()` with a variable, possibly-zero count never enters the CSS at
 * all. Each side is its own flex row (`.azoya-footer-band-side`) that
 * distributes whichever of its own zones are present equally between them —
 * one zone fills the whole side, two split it evenly (matching the original
 * fixed 5-column layout exactly when both sides are full), zero leaves it
 * blank but still exactly half-width, keeping the logo centered either way.
 *
 * Below 940px, `.azoya-footer-band-side` switches to `display: contents`
 * (see FooterBand.css) so its children rejoin the tablet/mobile wrapping
 * grid directly, exactly as if there were no side wrapper at all — those
 * breakpoints never had the centering problem (their tracks are uniform)
 * and are untouched by the desktop-only side/centering mechanism.
 */
export function FooterBand({
  contact,
  social,
  hours,
  partners,
  legal,
  copyright,
  logoSize = 120,
}: FooterBandProps) {
  const leftPetals: Petal[] = [
    { key: 'contact', content: contact },
    { key: 'social', content: social, className: 'azoya-footer-band-social' },
  ].filter((petal) => Boolean(petal.content))

  const rightPetals: Petal[] = [
    { key: 'hours', content: hours },
    { key: 'partners', content: partners, className: 'azoya-footer-band-partners' },
  ].filter((petal) => Boolean(petal.content))

  function renderPetal({ key, content, className }: Petal) {
    return (
      <div key={key} className={className ? `azoya-footer-band-petal ${className}` : 'azoya-footer-band-petal'}>
        {content}
      </div>
    )
  }

  return (
    <div className="azoya-footer-band">
      <LeafGlyph className="azoya-footer-band-watermark" />
      <div className="azoya-footer-band-wrap">
        <div className="azoya-footer-band-petals">
          <div className="azoya-footer-band-side azoya-footer-band-side-left">
            {leftPetals.map(renderPetal)}
          </div>
          <div className="azoya-footer-band-center">
            <Logo size={logoSize} tone="dark" />
          </div>
          <div className="azoya-footer-band-side azoya-footer-band-side-right">
            {rightPetals.map(renderPetal)}
          </div>
        </div>
        <div className="azoya-footer-band-legal-row">
          {copyright ? <div className="azoya-footer-band-copyright">{copyright}</div> : <div />}
          <div className="azoya-footer-band-legal-links">{legal}</div>
        </div>
      </div>
    </div>
  )
}
